// src/pages/LandingPage.jsx
import React, { Fragment, useEffect, useMemo, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import Login from "./Login";
import { Link, useHistory } from "react-router-dom";
import { useAPI } from "../context/ApiContext";
import { useTranslation } from "react-i18next";

const DEFAULT_AVATAR = "/assets/images/default_profile.png";

const LandingPage = () => {
  const { t } = useTranslation();
  const api = useAPI();
  const history = useHistory();

  const title = t("LandingWelcomeTitle") || "Nicelinks | ようこそ";
  const canonical = "https://nicelinks.net/landingpage";
  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const inviterId = qs.get("invited_by") || "";

  const [inviter, setInviter] = useState(null);
  const [loadingInviter, setLoadingInviter] = useState(!!inviterId);

  // Userpage と同じAPIでプロフィール解決
  const fetchInviter = useCallback(async (uid) => {
    if (!uid || typeof api?.getProfile !== "function") return null;
    try {
      const p = await api.getProfile(uid);
      if (p) return { userId: p.userId || uid, nickname: p.nickname || uid, image: p.image || DEFAULT_AVATAR };
    } catch {}
    return { userId: uid, nickname: uid, image: DEFAULT_AVATAR };
  }, [api]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!inviterId) return;
      setLoadingInviter(true);
      const info = await fetchInviter(inviterId);
      if (!alive) return;
      setInviter(info);
      setLoadingInviter(false);
    })();
    return () => { alive = false; };
  }, [inviterId, fetchInviter]);

  // モーダル表示（招待時のみ）
  const showInvite = !!inviterId;

  // Login の上余白（モーダルは中央固定なので 0 に）
  const topOffset = 0;

  const profileUrl = `/user/${encodeURIComponent(inviter?.userId || inviterId)}?fromInvite=1`;

  return (
    <Fragment>
      <Helmet>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta name="twitter:title" content={title} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* 背景（ログイン画面） */}
      <div className={showInvite ? "nl-blurred-bg" : ""}>
        <Login topOffset={topOffset} />
      </div>

      {/* 招待モーダル（中央表示 + 背景ブラー） */}
      {showInvite && (
        <>
          <style>{`
            /* 背景をブラーさせるための wrapper */
            .nl-blurred-bg { filter: blur(4px); transform: translateZ(0); }
            /* オーバーレイ（中央に配置） */
            .nl-invite-overlay {
              position: fixed; inset: 0; z-index: 100;
              display: grid; place-items: center;
              background: rgba(0,0,0,.35);
              backdrop-filter: blur(6px) saturate(110%);
            }
            .nl-invite-modal {
              width: min(720px, 94vw);
              border-radius: 20px;
              background: linear-gradient(135deg,#111827,#1f2937);
              color: #fff;
              box-shadow: 0 20px 60px rgba(0,0,0,.35);
              overflow: hidden;
            }
            .nl-invite-row {
              display: grid; grid-template-columns: auto minmax(0,1fr);
              gap: 14px; align-items: center; padding: 18px 18px 14px;
            }
            .nl-invite-avatar {
              width: 64px; height: 64px; border-radius: 18px;
              object-fit: cover; background:#222;
            }
            .nl-invite-title {
              font-weight: 800; font-size: 20px; line-height: 1.35;
              overflow-wrap: anywhere; word-break: break-word;
            }
            .nl-invite-desc { font-size: 13px; opacity:.9; margin-top: 6px; }
            .nl-invite-actions {
                  display: flex;
                  flex-direction: column;           /* 縦並び */
                  align-items: center;              /* 中央揃え */
                  gap: 12px;
                  padding: 0 18px 18px;
                }
                
                /* ボタン幅をそろえて中央に */
                .nl-invite-actions .nl-cta,
                .nl-invite-actions .nl-ghost {
                  width: min(420px, 100%);          /* 画面に応じて最大幅を制限 */
                  text-align: center;               /* 文字を中央揃え */
                  justify-content: center;          /* flexの中央揃え保険 */
                }
            .nl-cta {
              padding: 10px 16px; border-radius: 9999px; border: 0;
              background: #fff; color:#111; font-weight: 800;
              white-space: nowrap;
            }
            .nl-ghost {
              padding: 10px 14px; border-radius: 9999px;
              border: 1px solid rgba(255,255,255,.35);
              background: transparent; color:#fff; font-weight: 700;
            }
            .nl-close {
              position:absolute; top:10px; right:10px;
              width:36px; height:36px; border-radius:9999px;
              border:0; color:#fff; background:rgba(255,255,255,.12);
            }
            @media (max-width: 420px){
              .nl-invite-avatar{ width:56px; height:56px; border-radius:16px; }
              .nl-invite-title{ font-size:18px; }
            }
          `}</style>

          <div className="nl-invite-overlay" role="dialog" aria-modal="true" aria-label="invite dialog">
            <div className="nl-invite-modal">
              <button className="nl-close" aria-label="閉じる" onClick={() => history.replace("/login")}>
                ✕
              </button>

              <div className="nl-invite-row">
                <Link to={profileUrl} onClick={(e) => e.stopPropagation()}>
                  <img
                    src={(inviter && inviter.image) || DEFAULT_AVATAR}
                    alt="inviter"
                    className="nl-invite-avatar"
                    onError={(e) => { e.currentTarget.src = DEFAULT_AVATAR; }}
                  />
                </Link>
                <div>
                  <div className="nl-invite-title">
                    {loadingInviter
                      ? (t("InviteLoading") || "招待情報を読み込み中…")
                      : (<><strong>{(inviter && inviter.nickname) || inviterId}</strong>{t("InviteHero1") || " さんから招待されました！"}</>)
                    }
                  </div>
                  <div className="nl-invite-desc">
                    {t("InviteHero2") || "ログインすると、招待してくれたユーザーを見つけてフォローできます。"}
                  </div>
                </div>
              </div>

              <div className="nl-invite-actions">
                <button
                  className="nl-cta"
                  onClick={() =>
                    history.push(
                      inviterId
                        ? `/login?next=${encodeURIComponent(profileUrl)}`
                        : "/login"
                    )
                  }
                >
                  {t("InviteHeroCTA") || "ログインしてフォローへ"}
                </button>

                {/* Nicelinksとは？ → HowTo へ */}
                <Link to="/howto" className="nl-ghost" onClick={(e)=>e.stopPropagation()}>
                「Nicelinks」とは？
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </Fragment>
  );
};

export default LandingPage;
